import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ModernButton from '@/components/dashboard/ModernButton';
import ModernTabNavigation from '@/components/dashboard/ModernTabNavigation';
import { ModernDropdown } from '@/components/ui/modern-dropdown';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Activity,
  ArrowLeft,
  BarChart3,
  Bot,
  Building,
  Calendar,
  ChevronDown,
  Copy,
  CreditCard,
  Globe,
  GroupIcon,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  ReceiptEuroIcon,
  TrendingUp,
  User,
  Users,
  Zap,
  ZapOff,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import { useBusinessDetail } from '@/hooks/useBusinesses';
import { Icon } from '@/components/icons';
import { formatRelativeTime } from '@/lib/utils';
import { isRecord } from '@/lib/records';
import { useToast } from '@/components/ui/use-toast';
import { apiRequest } from '@/utils/api-interceptor';
import { getApiUrl } from '@/utils/api-config';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type AgentKnowledgeSource = {
  title?: string | null;
  extracted_characters?: number | string | null;
  type?: string | null;
};

type Agent = {
  id: number;
  name: string;
  agentType: string;
  agent_category: string;
  status: string;
  conversations: number;
  knowledge_sources?: AgentKnowledgeSource[];
};

type ConversationVolumeDatum = {
  week?: string;
  no_of_conversations?: number | string | null;
  no_of_tokens?: number | string | null;
};

type TransactionRecord = {
  payment_date?: string | null;
  transaction_date?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  amount?: number | string | null;
  status?: string | null;
  invoice_url?: string | null;
  plan_name?: string | null;
  addon_type?: string | null;
  quantity?: number | null;
  payment_type?: string | null;
  card_last4?: string | number | null;
  card_brand?: string | null;
  transaction_id?: string | number | null;
  transactionId?: string | number | null;
};

type TransactionHistoryApiResponse = {
  message?: string;
  status?: string;
  data?: {
    transactions?: TransactionRecord[];
    total_count?: number;
    page?: number;
    page_size?: number;
    total_pages?: number;
  };
};

const TRANSACTION_PAGE_SIZE = 5;

const DEFAULT_COST_VS_SUMMARY = {
  operational_cost: {
    day: 0,
    month: 0.06,
    year: 0.66,
  },
  subscription: {
    day: 6.67,
    month: 200,
    year: 2400,
  },
  difference: {
    day: 6.66,
    month: 199.94,
    year: 2399.34,
  },
};

type SummaryFieldKey = 'day' | 'month' | 'year';

const SUMMARY_FIELD_LABELS: Record<SummaryFieldKey, string> = {
  day: 'Daily total',
  month: 'Monthly total',
  year: 'Annual total',
};

const determineSummaryFieldKey = (hints: Array<string | undefined>): SummaryFieldKey => {
  const normalizedHints = hints
    .filter((hint): hint is string => typeof hint === 'string' && hint.trim().length > 0)
    .map((hint) => hint.toLowerCase());

  if (normalizedHints.some((hint) => hint.includes('year'))) {
    return 'year';
  }
  if (normalizedHints.some((hint) => hint.includes('month'))) {
    return 'month';
  }
  if (normalizedHints.some((hint) => hint.includes('week'))) {
    return 'day';
  }
  if (normalizedHints.some((hint) => hint.includes('day'))) {
    return 'day';
  }
  return 'month';
};

const parseNumericValue = (value?: unknown) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const sanitized = value.replace(/[^0-9.-]/g, '');
    const numeric = Number(sanitized);
    return Number.isFinite(numeric) ? numeric : 0;
  }
  return 0;
};

const getTotalExtractedCharacters = (sources?: AgentKnowledgeSource[]) => {
  if (!Array.isArray(sources)) return 0;
  return sources.reduce((total, source) => total + parseNumericValue(source?.extracted_characters), 0);
};

const mapCostRecordsToChartData = (
  records?: Record<string, number | string | null>
): Array<{ label: string; cost: number }> => {
  if (!records) return [];
  return Object.entries(records).map(([label, value]) => ({
    label,
    cost: parseNumericValue(value ?? 0),
  }));
};

const maskTransactionId = (value?: string | number | null) => {
  if (value === null || value === undefined) {
    return '--';
  }
  const stringValue = String(value);
  const revealed = stringValue.slice(-4) || stringValue;
  return `****${revealed}`;
};

const formatCurrency = (value?: number | string | null) => {
  if (value === null || value === undefined) {
    return '--';
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return '--';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
  }).format(numeric);
};

type CardBrandLogo = {
  src: string;
  alt: string;
};

const cardBrandLogoMap: Record<string, CardBrandLogo> = {
  visa: {
    src: 'https://upload.wikimedia.org/wikipedia/commons/1/12/Font_Awesome_5_brands_cc-visa.svg',
    alt: 'Visa',
  },
  mastercard: {
    src: 'https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg',
    alt: 'Mastercard',
  },
  'master card': {
    src: 'https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg',
    alt: 'Mastercard',
  },
  'american express': {
    src: 'https://upload.wikimedia.org/wikipedia/commons/3/30/American_Express_logo.svg',
    alt: 'American Express',
  },
  amex: {
    src: 'https://upload.wikimedia.org/wikipedia/commons/3/30/American_Express_logo.svg',
    alt: 'American Express',
  },
  discover: {
    src: 'https://upload.wikimedia.org/wikipedia/commons/e/e5/DiscoverCard.svg',
    alt: 'Discover',
  },
};

const renderCardBrandLogo = (brand?: string | null) => {
  if (!brand) return null;
  const normalized = brand.toLowerCase();
  const logo = cardBrandLogoMap[normalized];
  if (!logo) return null;
  return (
    <img
      src={logo.src}
      alt={`${logo.alt} card`}
      className="h-4 object-contain dark:invert"
      loading="lazy"
      decoding="async"
    />
  );
};

const BusinessDetail = () => {
  const { businessId } = useParams();
  const [activeAgentTab, setActiveAgentTab] = useState('human');
  
  // Refs for scroll sections
  const overviewRef = useRef<HTMLDivElement>(null);
  const agentsRef = useRef<HTMLDivElement>(null);
  const analyticsRef = useRef<HTMLDivElement>(null);
  const conversationsRef = useRef<HTMLDivElement>(null);
  const subscriptionRef = useRef<HTMLDivElement>(null);
  const costVsSubscriptionRef = useRef<HTMLDivElement>(null);
  const transactionsRef = useRef<HTMLDivElement>(null);
  const initialTransactionsRef = useRef<TransactionRecord[]>([]);
  
  const { data: businessData, isLoading, isError, error, refetch } = useBusinessDetail(businessId);
  const { toast } = useToast();
  const [isTogglingBusiness, setIsTogglingBusiness] = useState(false);
  const [isToggleDialogOpen, setIsToggleDialogOpen] = useState(false);
  const [costView, setCostView] = useState<'llms' | 'agents'>('llms');
  const [costVsView, setCostVsView] = useState<string>('');
  const [expandedAgents, setExpandedAgents] = useState<Record<number, boolean>>({});
  const [transactions, setTransactions] = useState<TransactionRecord[]>([]);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionTotalPages, setTransactionTotalPages] = useState(1);
  const [transactionsTotalCount, setTransactionsTotalCount] = useState(0);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isFetchingMoreTransactions, setIsFetchingMoreTransactions] = useState(false);
  const [lastFetchedTransactionCount, setLastFetchedTransactionCount] = useState(0);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleAgentKnowledge = (agentId: number) => {
    setExpandedAgents((prev) => ({
      ...prev,
      [agentId]: !prev[agentId],
    }));
  };

  const fetchTransactions = useCallback(
    async (page: number, { append = false }: { append?: boolean } = {}) => {
      if (!businessId) return;
      if (append) {
        setIsFetchingMoreTransactions(true);
      } else {
        setIsLoadingTransactions(true);
      }

      try {
        const url = getApiUrl(
          `admin/businesses/${businessId}/transaction-history/?page=${page}&page_size=${TRANSACTION_PAGE_SIZE}`
        );
        const response = await apiRequest(url);
        let payload: TransactionHistoryApiResponse | null = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }

        if (!response.ok) {
          const errorMessage = payload?.message || 'Failed to fetch transaction history.';
          throw new Error(errorMessage);
        }

        const data = payload?.data || {};
        const nextTransactions = Array.isArray(data.transactions) ? data.transactions : [];

        setTransactions((prev) => (append ? [...prev, ...nextTransactions] : [...nextTransactions]));
        if (!append) {
          initialTransactionsRef.current = [...nextTransactions];
        }
        setLastFetchedTransactionCount(nextTransactions.length);

        // The API can return its default page field rather than the requested page.
        setTransactionPage(page);
        setTransactionTotalPages(data.total_pages || 1);
        setTransactionsTotalCount(data.total_count || nextTransactions.length);
      } catch (err) {
        toast({
          title: 'Unable to load transactions',
          description: err instanceof Error ? err.message : 'Failed to fetch transaction history.',
          variant: 'destructive',
        });
      } finally {
        if (append) {
          setIsFetchingMoreTransactions(false);
        } else {
          setIsLoadingTransactions(false);
        }
      }
    },
    [businessId, toast]
  );

  useEffect(() => {
    if (!businessId) return;
    fetchTransactions(1);
  }, [businessId, fetchTransactions]);

  const handleShowMoreTransactions = async () => {
    if (transactionPage >= transactionTotalPages || isFetchingMoreTransactions) {
      return;
    }
    await fetchTransactions(transactionPage + 1, { append: true });
  };

  const handleShowLessTransactions = () => {
    if (transactionPage === 1) return;
    setTransactions([...initialTransactionsRef.current]);
    setTransactionPage(1);
    setLastFetchedTransactionCount(initialTransactionsRef.current.length);
  };

  const handleCopyTransactionId = async (value?: string | number | null) => {
    if (value === null || value === undefined) {
      return;
    }

    const stringValue = String(value);

    const copyWithExecCommand = () => {
      if (typeof document === 'undefined') {
        return false;
      }
      const textarea = document.createElement('textarea');
      textarea.value = stringValue;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      return successful;
    };

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(stringValue);
      } else if (!copyWithExecCommand()) {
        throw new Error('Copy not supported');
      }

      toast({
        title: 'Copied',
        description: 'Transaction ID copied to clipboard.',
      });
    } catch (err) {
      toast({
        title: 'Copy failed',
        description: 'Unable to copy the transaction ID.',
        variant: 'destructive',
      });
    }
  };


  const agentTabs = [
    { id: 'human', label: 'Team' },
    { id: 'ai', label: 'AI Agents' },
  ];

  const costViewOptions = [
    { value: 'llms', label: 'LLM Costs' },
    { value: 'agents', label: 'Agent Costs' }
  ];

  const fallbackConversationData: ConversationVolumeDatum[] = [
    { week: 'W1', no_of_conversations: 2800, no_of_tokens: 540000 },
    { week: 'W2', no_of_conversations: 3200, no_of_tokens: 610000 },
    { week: 'W3', no_of_conversations: 3500, no_of_tokens: 660000 },
    { week: 'W4', no_of_conversations: 3700, no_of_tokens: 705000 },
    { week: 'W5', no_of_conversations: 3400, no_of_tokens: 630000 },
    { week: 'W6', no_of_conversations: 3800, no_of_tokens: 720000 },
    { week: 'W7', no_of_conversations: 4100, no_of_tokens: 780000 },
    { week: 'W8', no_of_conversations: 4500, no_of_tokens: 840000 },
  ];
  const conversationData: ConversationVolumeDatum[] = businessData?.weekwise_conversation_volume || fallbackConversationData;

  const transactionHistory = transactions;
  const successfulTransactionStatuses = new Set(['paid', 'active', 'completed', 'success', 'fulfilled']);
  const llmCostData = mapCostRecordsToChartData(businessData?.llm_costs?.llm_cost);
  const agentCostData = mapCostRecordsToChartData(businessData?.llm_costs?.agent_cost);
  const totalLLMCost = llmCostData.reduce((sum, record) => sum + parseNumericValue(record?.cost), 0);
  const totalAgentCost = agentCostData.reduce((sum, record) => sum + parseNumericValue(record?.cost), 0);
  const rawCostVsSubscription = businessData?.cost_vs_subscription_statistics;
  

  const costVsSubscriptionPayload = useMemo<Record<string, unknown> | undefined>(() => {
    if (!rawCostVsSubscription) {
      return undefined;
    }
    if (typeof rawCostVsSubscription === 'string') {
      try {
        const parsed: unknown = JSON.parse(rawCostVsSubscription);
        if (isRecord(parsed)) return parsed;
        console.error('Invalid cost_vs_subscription payload', parsed);
        return undefined;
      } catch (parseError) {
        console.error('Failed to parse cost_vs_subscription payload', parseError);
        return undefined;
      }
    }
    return isRecord(rawCostVsSubscription) ? rawCostVsSubscription : undefined;
  }, [rawCostVsSubscription]);

  const parsedCostVsSummary = useMemo(() => {
    const summary = costVsSubscriptionPayload?.summary;
    if (!summary) {
      return undefined;
    }
    if (typeof summary === 'string') {
      try {
        const parsed: unknown = JSON.parse(summary);
        if (isRecord(parsed)) return parsed;
        console.error('Invalid cost_vs_subscription summary payload', parsed);
        return undefined;
      } catch (parseError) {
        console.error('Failed to parse cost_vs_subscription summary payload', parseError);
        return undefined;
      }
    }
    return isRecord(summary) ? summary : undefined;
  }, [costVsSubscriptionPayload]);

  const costVsViews = useMemo(() => {
    let resolvedViews: unknown = costVsSubscriptionPayload?.views;
    if (!resolvedViews) {
      return [];
    }

    if (typeof resolvedViews === 'string') {
      try {
        resolvedViews = JSON.parse(resolvedViews);
      } catch (parseError) {
        console.error('Failed to parse cost_vs_subscription views payload', parseError);
        return [];
      }
    }

    const entries: Array<{ id: string; label: string; view: Record<string, unknown> }> = [];

    const pushEntry = (fallbackKey: string, view: unknown, index: number) => {
      if (!view) {
        return;
      }
      if (!isRecord(view)) {
        console.error('Invalid cost_vs_subscription view', view);
        return;
      }
      const key =
        typeof view?.key === 'string' && view.key.trim().length > 0
          ? view.key.trim()
          : fallbackKey || `view-${index}`;
      const label =
        typeof view?.label === 'string' && view.label.trim().length > 0 ? view.label.trim() : key;

      entries.push({
        id: key,
        label,
        view,
      });
    };

    if (Array.isArray(resolvedViews)) {
      resolvedViews.forEach((view, index) => pushEntry(`view-${index + 1}`, view, index));
    } else if (isRecord(resolvedViews)) {
      Object.entries(resolvedViews).forEach(([key, view], index) =>
        pushEntry(key, view, index)
      );
    }

    return entries;
  }, [costVsSubscriptionPayload]);

  useEffect(() => {
    if (costVsViews.length === 0) {
      setCostVsView('');
      return;
    }
    setCostVsView((current) => {
      const exists = costVsViews.some((entry) => entry.id === current);
      return exists ? current : costVsViews[0].id;
    });
  }, [costVsViews]);

  const costVsDropdownOptions = useMemo(
    () => costVsViews.map((entry) => ({ value: entry.id, label: entry.label })),
    [costVsViews]
  );

  const resolvedCostVsViewKey = costVsView || costVsViews[0]?.id || '';

  const activeCostVsViewEntry = useMemo(
    () => costVsViews.find((entry) => entry.id === resolvedCostVsViewKey),
    [costVsViews, resolvedCostVsViewKey]
  );

  const activeCostVsView = activeCostVsViewEntry?.view;

  const costVsChartData = useMemo(() => {
    if (!activeCostVsView) {
      return [];
    }

    const labels = Array.isArray(activeCostVsView.labels) ? activeCostVsView.labels : [];
    const operationalCosts = Array.isArray(activeCostVsView.operational_cost)
      ? activeCostVsView.operational_cost
      : [];
    const subscriptionValues = Array.isArray(activeCostVsView.subscription)
      ? activeCostVsView.subscription
      : [];

    return labels.map((rawLabel, index) => ({
      label:
        typeof rawLabel === 'string' && rawLabel.length > 0 ? rawLabel : `Point ${index + 1}`,
      operational_cost: parseNumericValue(operationalCosts[index]),
      subscription: parseNumericValue(subscriptionValues[index]),
    }));
  }, [activeCostVsView]);

  const hasCostVsChartData = costVsChartData.length > 0;
  const selectedCostVsLabel =
    (typeof activeCostVsViewEntry?.label === 'string' && activeCostVsViewEntry.label.trim().length > 0
      ? activeCostVsViewEntry.label
      : typeof activeCostVsView?.label === 'string' && activeCostVsView.label.trim().length > 0
        ? activeCostVsView.label
        : undefined);

  const summaryFieldDetails = useMemo(() => {
    const hints = [
      resolvedCostVsViewKey,
      activeCostVsViewEntry?.label,
      typeof activeCostVsView?.label === 'string' ? activeCostVsView.label : undefined,
      typeof activeCostVsView?.key === 'string' ? activeCostVsView.key : undefined,
    ];
    const field = determineSummaryFieldKey(hints);
    const baseLabel = SUMMARY_FIELD_LABELS[field];
    const contextLabel = selectedCostVsLabel ? `${baseLabel} • ${selectedCostVsLabel}` : baseLabel;
    return { field, baseLabel, contextLabel };
  }, [
    activeCostVsView,
    activeCostVsViewEntry,
    resolvedCostVsViewKey,
    selectedCostVsLabel,
  ]);

  const summaryFieldKey = summaryFieldDetails.field;
  const summaryContextLabel = summaryFieldDetails.contextLabel;

  const costVsSummaryCards = useMemo(() => {
    const summary = parsedCostVsSummary ?? DEFAULT_COST_VS_SUMMARY;

    const formatSummaryValue = (value?: unknown) => {
      if (value === null || value === undefined) {
        return '--';
      }
      if (typeof value === 'string' && value.trim().length === 0) {
        return '--';
      }
      const numericValue = parseNumericValue(value);
      return formatCurrency(numericValue);
    };

    const cards = [
      {
        key: 'operational_cost',
        title: 'Operational Cost',
        accentClass: 'text-foreground',
        data: summary?.operational_cost,
      },
      {
        key: 'subscription',
        title: 'Subscription Spend',
        accentClass: 'text-foreground',
        data: summary?.subscription,
      },
      {
        key: 'difference',
        title: 'Cost Difference',
        accentClass: 'text-foreground',
        data: summary?.difference,
      },
    ];

    return cards
      .map((card) => ({
        key: card.key,
        title: card.title,
        accentClass: card.accentClass,
        value: formatSummaryValue(isRecord(card.data) ? card.data[summaryFieldKey] : undefined),
      }))
      .filter((card) => card.value !== '--');
  }, [parsedCostVsSummary, summaryFieldKey]);

  const hasCostVsSummary = costVsSummaryCards.length > 0;

  const { totalConversations, totalTokens, avgTokensPerConversation } = useMemo(() => {
    if (!Array.isArray(conversationData) || conversationData.length === 0) {
      return { totalConversations: 0, totalTokens: 0, avgTokensPerConversation: 0 };
    }
    const totals = conversationData.reduce(
      (acc, point) => {
        acc.totalConversations += parseNumericValue(point?.no_of_conversations as number | string | null);
        acc.totalTokens += parseNumericValue(point?.no_of_tokens as number | string | null);
        return acc;
      },
      { totalConversations: 0, totalTokens: 0 }
    );
    return {
      totalConversations: totals.totalConversations,
      totalTokens: totals.totalTokens,
      avgTokensPerConversation:
        totals.totalConversations > 0 ? totals.totalTokens / totals.totalConversations : 0,
    };
  }, [conversationData]);

  const renderConversationTooltip = useCallback(
    ({ active, payload }: TooltipProps<number, string>) => {
      if (!active || !payload || payload.length === 0) {
        return null;
      }
      const point = payload[0].payload as {
        week?: string;
        no_of_conversations?: number | string | null;
        no_of_tokens?: number | string | null;
      };
      const conversations = parseNumericValue(point?.no_of_conversations);
      const tokens = parseNumericValue(point?.no_of_tokens);
      const average = conversations > 0 ? tokens / conversations : 0;
      return (
        <div
          style={{
            backgroundColor: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
            color: 'hsl(var(--foreground))',
            fontSize: '12px',
            padding: '12px 16px',
          }}
        >
          <p style={{ fontWeight: 600, marginBottom: 4 }}>{point?.week || 'Week'}</p>
          <p style={{ margin: 0 }}>Conversations: {conversations.toLocaleString()}</p>
          <p style={{ margin: 0 }}>Tokens: {tokens.toLocaleString()}</p>
          <p style={{ margin: 0 }}>
            Avg tokens/convo:{' '}
            {average > 0 ? average.toLocaleString(undefined, { maximumFractionDigits: 2 }) : 'N/A'}
          </p>
        </div>
      );
    },
    []
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
        <div className="container max-w-7xl mx-auto p-6 space-y-8">
          {/* Header Skeleton */}
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <div className="space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
          
          {/* Main Content Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Card className="rounded-2xl">
                <CardHeader>
                  <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            </div>
            
            <div className="space-y-6">
              <Card className="rounded-2xl">
                <CardHeader>
                  <Skeleton className="h-6 w-32" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !businessData) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
        <div className="container max-w-7xl mx-auto p-6 space-y-8">
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <div className="text-center py-10">
                <div className="text-destructive mb-2">
                  {isError ? 'Error loading business details' : 'Business not found'}
                </div>
                <p className="text-muted-foreground">
                  {error instanceof Error ? error.message : 'The requested business could not be found.'}
                </p>
                <ModernButton asChild className="mt-4">
                  <Link to="/businesses">Back to Businesses</Link>
                </ModernButton>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Extract business info from the detailed response
  const business = {
    name: businessData.business_info.name,
    domain: businessData.business_info.domain,
    plan: businessData.subscription.plan,
    status: businessData.subscription.status,
    admins: businessData.account_statistics.admins,
    agents: businessData.account_statistics.agents,
    created: businessData.business_info.account_created,
    email: businessData.business_info.email,
    phone: businessData.business_info.phone,
    businessStatus: businessData.business_info.status,
    sectorActivity: businessData.business_info.sector_activity,
    companySize: businessData.business_info.company_size
  };
  const formatDate = (rawDate?: string | null) => {
    if (!rawDate) return '--';
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) {
      return '--';
    }
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getStatusBadgeVariant = (status?: string | null) => {
    const normalized = (status || '').toLowerCase();
    if (['paid', 'active', 'completed', 'success'].includes(normalized)) {
      return 'success';
    }
    if (['pending', 'processing'].includes(normalized)) {
      return 'secondary';
    }
    if (['failed', 'cancelled', 'canceled', 'refunded'].includes(normalized)) {
      return 'destructive';
    }
    return 'outline';
  };

  const totalRevenue = transactionHistory.reduce((total, entry) => {
    const status = typeof entry?.status === 'string' ? entry.status.toLowerCase() : '';
    if (!successfulTransactionStatuses.has(status)) {
      return total;
    }
    return total + parseNumericValue(entry?.amount);
  }, 0);

  const marginRevenueStatuses = new Set(['active', 'fulfilled']);
  const marginRevenue = transactionHistory.reduce((total, entry) => {
    const status = typeof entry?.status === 'string' ? entry.status.toLowerCase() : '';
    if (!marginRevenueStatuses.has(status)) {
      return total;
    }
    return total + parseNumericValue(entry?.amount);
  }, 0);
  const marginProfit = marginRevenue - totalAgentCost;
  const marginHasRevenue = marginRevenue > 0;
  const marginIsPositive = marginProfit >= 0;
  const marginProfitPercent = marginHasRevenue ? (marginProfit / marginRevenue) * 100 : 0;
  const marginIndicatorWidth = marginHasRevenue ? Math.min(100, Math.abs(marginProfitPercent)) : 0;
  const llmCostCount = llmCostData.length;
  const agentCostCount = agentCostData.length;
  const currentCostData = costView === 'llms' ? llmCostData : agentCostData;
  const currentCostCount = costView === 'llms' ? llmCostCount : agentCostCount;
  const currentCostCategoryLabel = costView === 'llms' ? 'LLM' : 'agent';
  const currentCostEntityNoun = costView === 'llms' ? 'model' : 'agent';
  const currentCostEntityLabel = currentCostCount === 1 ? currentCostEntityNoun : `${currentCostEntityNoun}s`;
  const revenueSummaryLabel = totalRevenue > 0 ? formatCurrency(totalRevenue) : null;
  const hasChartData = currentCostData.length > 0;
  const normalizedStatus = business.businessStatus || '';
  const isBusinessDisabled = normalizedStatus === 'Disabled';
  const toggleButtonLabel = isBusinessDisabled ? 'Enable' : 'Disable';
  const togglePendingLabel = isBusinessDisabled ? 'Enabling...' : 'Disabling...';
  const toggleDialogTitle = isBusinessDisabled ? 'Enable business?' : 'Disable business?';
  const toggleDialogDescription = isBusinessDisabled
    ? 'Re-enabling will give this team access to their workspace again.'
    : 'Disabling immediately locks the workspace and prevents any new activity.';
  const toggleDialogConfirmLabel = isBusinessDisabled ? 'Enable Business' : 'Disable Business';
  const toggleActionClassName = isBusinessDisabled
    ? 'dark:text-black'
    : 'bg-destructive text-destructive-foreground hover:bg-destructive/90';

  const handleToggleDialogChange = (open: boolean) => {
    if (isTogglingBusiness) return;
    setIsToggleDialogOpen(open);
  };

  const handleToggleBusiness = async () => {
    if (!businessId) return;
    const action = isBusinessDisabled ? 'enable' : 'disable';
    setIsTogglingBusiness(true);

    try {
      const response = await apiRequest(getApiUrl(`admin/businesses/${businessId}/${action}/`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      let payload: { message?: string; error?: { message?: string } } | null = null;
      try {
        payload = await response.json();
      } catch (parseErr) {
        payload = null;
      }

      if (!response.ok) {
        const errorMessage = payload?.error?.message || payload?.message || `Failed to ${action} business.`;
        throw new Error(errorMessage);
      }

      toast({
        title: 'Success',
        description: payload?.message || `Business ${action}d successfully.`,
      });

      await refetch();
      setIsToggleDialogOpen(false);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update business status.',
        variant: 'destructive',
      });
    } finally {
      setIsTogglingBusiness(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-neutral-100/50 dark:bg-[hsla(0,0%,0%,0.95)]">
        <div className="container max-w-7xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ModernButton variant="ghost" size="sm" asChild>
              <Link to="/businesses" className="flex items-center">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Link>
            </ModernButton>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 capitalize">
                {business.name || 'Business Name'}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ModernButton
              size="sm"
              onClick={() => setIsToggleDialogOpen(true)}
              disabled={isTogglingBusiness}
              aria-busy={isTogglingBusiness}
            >
              {isBusinessDisabled ? (
                <Zap className="h-4 w-4 mr-2" />
              ) : (
                <ZapOff className="h-4 w-4 mr-2" />
              )}
              {isTogglingBusiness ? togglePendingLabel : toggleButtonLabel}
            </ModernButton>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Scrollable Content */}
          <div className="lg:col-span-2 space-y-8 overflow-y-auto">
            {/* Business Overview */}
            <div ref={overviewRef}>
              <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-lg pl-0 font-semibold text-neutral-900 dark:text-neutral-100">
                    Business Overview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="bg-transparent rounded-xl flex items-center justify-start bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-3">
                          <Globe className="h-5 w-5 text-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Domain</p>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400">
                            {business.domain || 'No domain set'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="bg-transparent rounded-xl flex items-center justify-start bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-3">
                          <Users className="h-5 w-5 text-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Admins</p>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400">
                            {business.admins} administrator{business.admins !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="bg-transparent rounded-xl flex items-center justify-start bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-3">
                          <Bot className="h-5 w-5 text-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Agents</p>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400">
                            {business.agents} active agent{business.agents !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="bg-transparent rounded-xl flex items-center justify-start bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-3">
                          <Building className="h-5 w-5 text-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Industry</p>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400">
                            {business.sectorActivity || 'No industry set'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="bg-transparent rounded-xl flex items-center justify-start bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-3">
                          <Calendar className="h-5 w-5 text-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Created</p>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400">
                            {new Date(business.created).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="bg-transparent rounded-xl flex items-center justify-start bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-3">
                          <CreditCard className="h-5 w-5 text-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Current Plan</p>
                          <Badge 
                            variant={
                              business.plan?.toLowerCase() === 'premium' ? 'default' : 
                              business.plan?.toLowerCase() === 'pro' ? 'secondary' : 
                              'outline'
                            } 
                            className="capitalize mt-1"
                          >
                            {business.plan || "No plan"}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="bg-transparent rounded-xl flex items-center justify-start bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-3">
                          <Activity className="h-5 w-5 text-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Status</p>
                          <Badge variant={isBusinessDisabled ? 'destructive' : 'success'} className="capitalize mt-1">
                            {isBusinessDisabled ? 'Disabled' : 'Active'}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="bg-transparent rounded-xl flex items-center justify-start bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-3">
                          <Users className="h-5 w-5 text-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Company Size</p>
                           <p className="text-sm text-neutral-600 dark:text-neutral-400">{businessData.business_info.company_size || 'No size set'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Agents Section */}
            <div ref={agentsRef}>
              <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg pl-0 font-semibold text-neutral-900 dark:text-neutral-100">
                      Team & Agents
                    </CardTitle>
                    <ModernTabNavigation
                      tabs={agentTabs}
                      activeTab={activeAgentTab}
                      onTabChange={setActiveAgentTab}
                      className="text-xs"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  {activeAgentTab === 'human' ? (
                    <div className="space-y-4">
                      {businessData.team.map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-800/30 rounded-2xl">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
                              <User className="h-4 w-4 text-white" />
                            </div>
                            <div>
                              <p className="font-medium text-neutral-900 dark:text-neutral-100">{member.username}</p>
                              <p className="text-sm text-neutral-600 dark:text-neutral-400">{member.email}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="capitalize">
                            {member.user_role}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {businessData.agents.map((agent: Agent) => {
                        const knowledgeSources: AgentKnowledgeSource[] = Array.isArray(agent.knowledge_sources)
                          ? agent.knowledge_sources
                          : [];
                        const extractedCharacters = getTotalExtractedCharacters(knowledgeSources);
                        const isExpanded = Boolean(expandedAgents[agent.id]);
                        const panelId = `agent-knowledge-${agent.id}`;
                        const normalizedStatus = (agent.status || '').toLowerCase();
                        const badgeVariant = normalizedStatus === 'active' ? 'success' : 'outline';
                        const canToggleKnowledge = knowledgeSources.length > 0 && extractedCharacters > 0;
                        const agentCategoryLabel = agent.agent_category || 'Uncategorized';
                        const agentTypeLabel = agent.agentType ? agent.agentType.replace(/_/g, ' ') : '';

                        return (
                          <div key={agent.id} className="p-4 bg-neutral-50 dark:bg-neutral-800/30 rounded-2xl">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600">
                                  <Bot className="h-4 w-4 text-white" />
                                </div>
                                <div>
                                  <p className="font-medium text-neutral-900 dark:text-neutral-100">{agent.name}</p>
                                  <p className="text-sm text-neutral-600 dark:text-neutral-400 capitalize">
                                    {agentCategoryLabel}
                                    {agentTypeLabel ? ` • ${agentTypeLabel}` : ''}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge 
                                  variant={badgeVariant}
                                  className="capitalize"
                                >
                                  {agent.status}
                                </Badge>
                                {canToggleKnowledge ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleAgentKnowledge(agent.id)}
                                    className="text-sm text-neutral-700 dark:text-neutral-200 font-medium inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-neutral-100/80 dark:hover:bg-neutral-700/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-900 transition-colors"
                                    aria-expanded={isExpanded}
                                    aria-controls={panelId}
                                  >
                                    <span>{extractedCharacters.toLocaleString()} chars • {agent.conversations} chats</span>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                  </button>
                                ) : (
                                  <span className="text-sm text-neutral-500 dark:text-neutral-400">
                                    {extractedCharacters.toLocaleString()} chars • {agent.conversations} chats
                                  </span>
                                )}
                              </div>
                            </div>
                            {canToggleKnowledge && isExpanded && (
                              <div
                                id={panelId}
                                className="mt-3 border-t border-neutral-200 dark:border-neutral-700 pt-3 text-sm"
                              >
                                {knowledgeSources.length > 0 ? (
                                  <div className="space-y-2">
                                    {knowledgeSources.map((source, index) => {
                                      const sourceChars = parseNumericValue(source?.extracted_characters);
                                      return (
                                        <div
                                          key={`${agent.id}-knowledge-${index}`}
                                          className="flex items-start justify-between gap-3"
                                        >
                                          <div>
                                            <p className="font-medium text-neutral-900 dark:text-neutral-100">
                                              {source?.title || 'Untitled source'}
                                            </p>
                                            <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                              Type: {source?.type || '--'}
                                            </p>
                                          </div>
                                          <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                            {sourceChars.toLocaleString()} chars
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                    No knowledge sources linked to this agent.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Analytics Section */}
            <div ref={analyticsRef}>
              <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-lg pl-0 font-semibold text-neutral-900 dark:text-neutral-100">
                    Analytics Overview
                  </CardTitle>
                  <CardDescription>
                    Current month's performance metrics
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-800 rounded-2xl">
                      <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                        {businessData.account_statistics.conversations}
                      </div>
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">Conversations</div>
                    </div>
                    <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-800 rounded-2xl">
                      <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                        {businessData.account_statistics.admins + businessData.account_statistics.agents}
                      </div>
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">Active Users</div>
                    </div>
                    <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-800 rounded-2xl">
                      <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                        {businessData.account_statistics.documents}
                      </div>
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">Knowledge Sources</div>
                    </div>
                  </div>
                  <div className="mt-6 p-6 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white/80 dark:bg-neutral-900/30">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Margin Analytics</p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                          Active & fulfilled revenue compared to agent spend
                        </p>
                      </div>
                      <div className={`text-sm font-semibold ${marginIsPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {marginHasRevenue ? `${marginIsPositive ? '+' : '-'}${Math.abs(marginProfitPercent).toFixed(1)}%` : 'N/A'}
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Revenue</p>
                        <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                          {formatCurrency(marginRevenue)}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">Active & fulfilled transactions</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Agent Cost</p>
                        <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                          {formatCurrency(totalAgentCost)}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">Sum of agent costs</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Profit</p>
                        <p className={`text-lg font-semibold ${marginIsPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {formatCurrency(marginProfit)}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">Revenue - agent cost</p>
                      </div>
                    </div>
                    
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Cost Breakdown */}
            <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg pl-0 font-semibold text-neutral-900 dark:text-neutral-100">
                      Cost Breakdown
                    </CardTitle>
                    <CardDescription>
                      {currentCostCount > 0
                        ? `View average spend across ${currentCostCount} ${currentCostEntityLabel}`
                        : `No ${currentCostCategoryLabel} cost data available yet`}
                      {revenueSummaryLabel ? ` • Revenue to date ${revenueSummaryLabel}` : ''}
                    </CardDescription>
                  </div>
                  <ModernDropdown
                    value={costView}
                    onValueChange={(value) => setCostView(value as 'llms' | 'agents')}
                    options={costViewOptions}
                    className="w-36 text-xs"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {hasChartData ? (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={currentCostData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 12 }}
                          className="text-neutral-600 dark:text-neutral-400"
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tickFormatter={(value) => `${value}`}
                          tick={{ fontSize: 12 }}
                          className="text-neutral-600 dark:text-neutral-400"
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          formatter={(rawValue: number | string) => {
                            const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue) || 0;
                            return [`EUR ${numericValue.toLocaleString()}`, 'Cost'];
                          }}
                          cursor={{ fill: 'transparent' }}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '12px',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                            color: 'hsl(var(--foreground))',
                            fontSize: '12px'
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Bar
                          dataKey="cost"
                          name={`${currentCostCategoryLabel} costs`}
                          fill="hsl(var(--primary))"
                          radius={[8, 8, 4, 4]}
                          barSize={32}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-sm text-neutral-500 dark:text-neutral-400">
                    No {currentCostCategoryLabel} cost data to display.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cost vs Subscription */}
            <div ref={costVsSubscriptionRef}>
              <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl">
                <CardHeader>
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <CardTitle className="text-lg pl-0 font-semibold text-neutral-900 dark:text-neutral-100">
                        Cost vs Subscription
                      </CardTitle>
                      <CardDescription>
                        Compare operational usage with subscription spend over time.
                      </CardDescription>
                      
                    </div>
                    {costVsDropdownOptions.length > 0 && (
                      <div className='flex flex-col gap-1 items-end'>
                      <ModernDropdown
                        value={resolvedCostVsViewKey}
                        onValueChange={(value) => setCostVsView(value)}
                        options={costVsDropdownOptions}
                        className="w-full md:w-44 text-xs"
                      />
                      {selectedCostVsLabel && (
                        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 pr-2">
                          Showing {selectedCostVsLabel === "Year (12 months)" ? "Past 12 months" : (selectedCostVsLabel === "Month (30 days)" ? "Past 30 days" : "Past 7 days")}
                        </p>
                      )}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {hasCostVsChartData ? (
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={costVsChartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 12 }}
                            className="text-neutral-600 dark:text-neutral-400"
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis
                            tickFormatter={(value) => formatCurrency(value as number)}
                            tick={{ fontSize: 12 }}
                            className="text-neutral-600 dark:text-neutral-400"
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            formatter={(raw, name, entry) => {
                              const numericValue = typeof raw === 'number' ? raw : Number(raw) || 0;
                              const dataKey = typeof entry?.dataKey === 'string' ? entry.dataKey : '';
                              const label = dataKey === 'subscription' || (typeof name === 'string' && name.toLowerCase() === 'subscription')
                                ? 'Subscription'
                                : 'Operational Cost';
                              return [formatCurrency(numericValue), label];
                            }}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '12px',
                              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                              color: 'hsl(var(--foreground))',
                              fontSize: '12px',
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: '12px' }} />
                          <Line
                            type="monotone"
                            dataKey="operational_cost"
                            stroke="hsl(var(--primary))"
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            name="Operational Cost"
                          />
                          <Line
                            type="monotone"
                            dataKey="subscription"
                            stroke="#10b981"
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            name="Subscription"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-sm text-neutral-500 dark:text-neutral-400">
                      Not enough cost vs subscription data to visualize yet.
                    </div>
                  )}
                  {hasCostVsSummary && (
                    <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                      {costVsSummaryCards.map((card) => (
                        <div
                          key={card.key}
                          className="rounded-2xl border border-neutral-200/80 bg-white/80 p-4 text-sm dark:border-neutral-700/70 dark:bg-neutral-900/20"
                        >
                          <p className={`text-xs font-semibold uppercase tracking-wide ${card.accentClass}`}>
                            {card.title}
                          </p>
                          <p className="mt-2 text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
                            {card.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Conversations Volume Chart */}
            <div ref={conversationsRef}>
              <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-lg pl-0 font-semibold text-neutral-900 dark:text-neutral-100">
                    Conversations Volume
                  </CardTitle>
                  <CardDescription>
                    Daily conversation trends over the last 30 days
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6 rounded-2xl border border-neutral-200/80 bg-white/70 p-4 text-center dark:border-neutral-800/80 dark:bg-neutral-900/40">
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                      Average tokens per conversation
                    </p>
                    <p className="mt-2 text-3xl font-semibold text-neutral-900 dark:text-neutral-100">
                      {avgTokensPerConversation > 0
                        ? avgTokensPerConversation.toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : 'N/A'}
                    </p>
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      {totalTokens.toLocaleString()} tokens • {totalConversations.toLocaleString()} conversations
                    </p>
                  </div>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={conversationData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <defs>
                          <linearGradient id="conversationGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                        <XAxis 
                          dataKey="week" 
                          tick={{ fontSize: 12, fill: 'currentColor' }}
                          className="text-neutral-600 dark:text-neutral-400"
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: 'currentColor' }}
                          className="text-neutral-600 dark:text-neutral-400"
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip content={renderConversationTooltip} />
                        <Area 
                          type="monotone" 
                          dataKey="no_of_conversations" 
                          stroke="#8884d8" 
                          fill="url(#conversationGradient)" 
                          fillOpacity={1} 
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Subscription */}
            <div ref={subscriptionRef}>
              <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-lg pl-0 font-semibold text-neutral-900 dark:text-neutral-100">
                    Subscription
                  </CardTitle>
                  <CardDescription>
                    Current subscription details
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="bg-transparent rounded-xl flex items-center justify-start bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-3">
                          <Icon name={`Star`} color='hsl(var(--primary))' type='plain' />
                        </div>
                        <div>
                          <p className="font-medium text-neutral-900 dark:text-neutral-100">
                            {businessData.subscription.plan || '--'}
                          </p>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400">Current subscription</p>
                        </div>
                      </div>
                      <Badge variant="default">{businessData.subscription.status || '--'}</Badge>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                      <span className="text-neutral-600 dark:text-neutral-400">Next billing</span>
                      <span className="text-neutral-900 dark:text-neutral-100">
                        {businessData.subscription.next_billing_date ? 
                          new Date(businessData.subscription.next_billing_date).toLocaleDateString() : 
                          'N/A'
                        }
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                      <span className="text-neutral-600 dark:text-neutral-400">Billing cycle</span>
                      <span className="text-neutral-900 dark:text-neutral-100 capitalize">
                        {businessData.subscription.billing_cycle || '--'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Transaction History */}
            <div ref={transactionsRef}>
              <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-lg pl-0 font-semibold text-neutral-900 dark:text-neutral-100">
                    Transaction History
                  </CardTitle>
                  <CardDescription>Recent payments for this business</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-neutral-500 dark:text-neutral-400 border-b border-neutral-200/60 dark:border-neutral-700/60">
                          <th className="py-2 pr-4 font-medium">Payment Date</th>
                          <th className="py-2 pr-4 font-medium">Payment Info</th>
                          <th className="py-2 pr-4 font-medium">Subscription Period</th>
                          <th className="py-2 pr-4 font-medium">Amount</th>
                          <th className="py-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoadingTransactions ? (
                          <tr>
                            <td colSpan={6} className="py-6 text-center text-neutral-500 dark:text-neutral-400">
                              Loading transaction history...
                            </td>
                          </tr>
                        ) : transactionHistory.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="py-6 text-center text-neutral-500 dark:text-neutral-400">
                              No transactions recorded yet.
                            </td>
                          </tr>
                        ) : (
                          transactionHistory.map((entry, index) => {
                            const paymentDate = formatDate(entry.payment_date || entry.transaction_date);
                            const subscriptionStart = formatDate(entry.start_date);
                            const subscriptionEnd = formatDate(entry.end_date);
                            const planName = entry.plan_name || '--';
                            const amount = formatCurrency(entry.amount);
                            const status = entry.status || '--';
                            const transactionId = entry.transaction_id || entry.transactionId || null;
                            const maskedTransactionId = maskTransactionId(transactionId);
                            const cardDigits = entry.card_last4 ? String(entry.card_last4).slice(-4) : null;
                            const cardBrandLogo = renderCardBrandLogo(entry.card_brand);
                            const rowKey =
                              transactionId ||
                              entry.invoice_url ||
                              `${entry.payment_date || entry.transaction_date || 'transaction'}-${index}`;

                            return (
                              <tr key={rowKey} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                                <td className="py-3 pr-4 text-neutral-900 dark:text-neutral-100">{paymentDate}</td>
                                {
                                  cardDigits && transactionId ? (
                                    <td className="py-3 pr-4 text-neutral-900 dark:text-neutral-100">
                                      <div className="flex items-center gap-2 font-mono">
                                        <span>{maskedTransactionId}</span>
                                        {transactionId ? (
                                          <button
                                            type="button"
                                            onClick={() => handleCopyTransactionId(transactionId)}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-900"
                                            aria-label="Copy transaction ID"
                                          >
                                            <Copy className="h-3.5 w-3.5" />
                                          </button>
                                        ) : null}
                                      </div>
                                      <div className="mt-1 flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-400">
                                        {cardBrandLogo || <CreditCard className="h-4 w-4" />}
                                        <span className="font-mono">{cardDigits || '--'}</span>
                                      </div>
                                    </td>
                                  ) : (<td>--</td>)
                                }
                                <td className="py-3 pr-4 text-neutral-600 dark:text-neutral-400 text-sm">
                                  {subscriptionStart} → {subscriptionEnd}
                                  <br/>
                                  <strong>{planName}</strong>
                                </td>
                                
                                <td className="py-3 pr-4 text-neutral-900 dark:text-neutral-100 font-medium">{amount}</td>
                               
                                <td className="py-3">
                                  <Badge variant={getStatusBadgeVariant(status)} className="uppercase tracking-tight">
                                    {status}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  {transactionHistory.length > 0 && (
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        Showing {transactionHistory.length} of {transactionsTotalCount || transactionHistory.length} transactions
                      </p>
                      <div className="flex gap-2">
                        {transactionPage > 1 && (
                          <ModernButton
                            variant="ghost"
                            size="sm"
                            onClick={handleShowLessTransactions}
                            disabled={isLoadingTransactions || isFetchingMoreTransactions}
                          >
                            Show Less
                          </ModernButton>
                        )}
                        {transactionPage < transactionTotalPages &&
                          lastFetchedTransactionCount === TRANSACTION_PAGE_SIZE && (
                          <ModernButton
                            size="sm"
                            onClick={handleShowMoreTransactions}
                            disabled={isFetchingMoreTransactions}
                            aria-busy={isFetchingMoreTransactions}
                          >
                            {isFetchingMoreTransactions ? 'Loading...' : 'Show More'}
                          </ModernButton>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Column - Sticky Sidebar */}
          <div className="lg:sticky lg:top-6 lg:h-fit space-y-6">
            {/* Quick Links */}
            <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl">
              <CardHeader>
                <CardTitle className="text-lg pl-0 font-semibold text-neutral-900 dark:text-neutral-100">
                  Quick Links
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <button
                  onClick={() => scrollToSection(overviewRef)}
                  className="w-full text-left px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800/30 rounded-lg transition-colors"
                >
                  <BarChart3 className="h-4 w-4 inline mr-2" />
                  Business Overview
                </button>
                <button
                  onClick={() => scrollToSection(agentsRef)}
                  className="w-full text-left px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800/30 rounded-lg transition-colors"
                >
                  <Users className="h-4 w-4 inline mr-2" />
                  Team & Agents
                </button>
                <button
                  onClick={() => scrollToSection(analyticsRef)}
                  className="w-full text-left px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800/30 rounded-lg transition-colors"
                >
                  <Activity className="h-4 w-4 inline mr-2" />
                  Analytics
                </button>
                <button
                  onClick={() => scrollToSection(costVsSubscriptionRef)}
                  className="w-full text-left px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800/30 rounded-lg transition-colors"
                >
                  <TrendingUp className="h-4 w-4 inline mr-2" />
                  Cost vs Subscription
                </button>
                <button
                  onClick={() => scrollToSection(conversationsRef)}
                  className="w-full text-left px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800/30 rounded-lg transition-colors"
                >
                  <MessageSquare className="h-4 w-4 inline mr-2" />
                  Conversations
                </button>
                <button
                  onClick={() => scrollToSection(subscriptionRef)}
                  className="w-full text-left px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800/30 rounded-lg transition-colors"
                >
                  <CreditCard className="h-4 w-4 inline mr-2" />
                  Subscription
                </button>
                <button
                  onClick={() => scrollToSection(transactionsRef)}
                  className="w-full text-left px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800/30 rounded-lg transition-colors"
                >
                  <ReceiptEuroIcon className="h-4 w-4 inline mr-2" />
                  Transaction History
                </button>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl">
              <CardHeader>
                <CardTitle className="text-lg pl-0 font-semibold text-neutral-900 dark:text-neutral-100">
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-neutral-500" />
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Email</p>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      {business.email || `contact@${business.domain || 'business.com'}`}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-neutral-500" />
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Phone</p>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      {business.phone || '+1 (555) 123-4567'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Activity Summary */}
            <Card className="bg-white dark:bg-neutral-800/60 border-0 rounded-2xl">
              <CardHeader>
                <CardTitle className="text-lg pl-0 font-semibold text-neutral-900 dark:text-neutral-100">
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center p-2 bg-neutral-50 dark:bg-neutral-800/30 rounded-lg">
                    <span className="text-neutral-600 dark:text-neutral-400">Last login</span>
                    <span className="text-neutral-900 dark:text-neutral-100">{formatRelativeTime(businessData.recent_activities?.last_login)}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-neutral-50 dark:bg-neutral-800/30 rounded-lg">
                    <span className="text-neutral-600 dark:text-neutral-400">Agents updated</span>
                    <span className="text-neutral-900 dark:text-neutral-100">{formatRelativeTime(businessData.recent_activities?.agent_updated)}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-neutral-50 dark:bg-neutral-800/30 rounded-lg">
                    <span className="text-neutral-600 dark:text-neutral-400">Plan changed</span>
                    <span className="text-neutral-900 dark:text-neutral-100">{formatRelativeTime(businessData.recent_activities?.subscription_changed)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>

    <AlertDialog open={isToggleDialogOpen} onOpenChange={handleToggleDialogChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{toggleDialogTitle}</AlertDialogTitle>
          <AlertDialogDescription>{toggleDialogDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isTogglingBusiness}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleToggleBusiness}
            disabled={isTogglingBusiness}
            className={toggleActionClassName}
          >
            {isTogglingBusiness && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {toggleDialogConfirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default BusinessDetail;
